import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Tooltip, Input } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { ExcelExportColumn, toDataURL } from "@progress/kendo-react-excel-export";
import dayjs from "../../../../../util/dayjs-config";
import { normalizeDate, removeDuplicateHeaderRows } from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import EmptyState from "../../../../../assets/img/Errors/empty.svg";
import PropTypes from "prop-types";

export const TechBankerIdBadge = ({ text }) => {
  return (
    <FluentBadge
      shape="rounded"
      appearance="tint"
      size="medium"
      color="success"
      text={text}
    />
  );
};

TechBankerIdBadge.propTypes = {
  text: PropTypes.string.isRequired,
};

export const getBankerTimeLogFileName = () => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  return `Banker Time Log ${timestamp}.xlsx`;
};

export const handleExport = async ({ summaryRef, detailRef }) => {
  if (!summaryRef.current || !detailRef.current) return;

  const fileName = getBankerTimeLogFileName();

  const summaryWorkbook = summaryRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Summary";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = detailRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


export const isRowEdited = (original, edited) => {
  return (
    original.LastWeekHours !== edited.LastWeekHours ||
    original.WeekendHours !== edited.WeekendHours ||
    original.CurrentWeekHours !== edited.CurrentWeekHours
  );
};

export const getEditedRows = (data, originalData) =>
  data.filter((editedRow, index) => isRowEdited(originalData[index], editedRow));

export const buildPayload = (rows) =>
  rows.map(({ TaskId, ProjectId, LastWeekHours, WeekendHours, CurrentWeekHours }) => ({
    TaskId,
    ProjectId,
    LastWeekHours,
    WeekendHours,
    CurrentWeekHours,
  }));

export const mergeUpdatedData = (data, updatedProjects) => {
  const updatedMap = new Map(updatedProjects.map((p) => [p.ProjectId, p]));

  return data.map((row) => {
    const match = updatedMap.get(row.ProjectId);
    return match
      ? {
          ...row,
          TotalHours: match.TotalHours,
          LastUpdated: normalizeDate(match.LastUpdated),
          LastWeekHours: match.LastWeekHours,
          WeekendHours: match.WeekendHours,
          CurrentWeekHours: match.CurrentWeekHours,
        }
      : row;
  });
};

export const getSummaryColumns = () => (
  <>
    <ExcelExportColumn field="User.Id" title="User ID" />
    <ExcelExportColumn field="User.Name" title="Analyst/Associate" />
    <ExcelExportColumn field="TotalLastWeekHours" title="Total Last Week Hours" />
    <ExcelExportColumn field="TotalWeekendHours" title="Total Weekend Hours" />
    <ExcelExportColumn field="AllDealsHours" title="All Deals Hours" />
    <ExcelExportColumn field="TotalCurrentWeekHours" title="Total Current Week Hours" />
    <ExcelExportColumn
      field="LastUpdated"
      title="Last Updated"
      cellOptions={{ format: "mm/dd/yyyy" }}
    />
  </>
);

export const computeExportData = (data) =>
  data.map((row) => ({
    ...row,
    OwnersCSV: (row.Members || [])
      .filter((m) => m.IsOwner)
      .map((m) => m.User?.Name ?? "")
      .filter(Boolean)
      .join(", "),
    DealTeamCSV: (row.Members || [])
      .filter((m) => !m.IsOwner)
      .map((m) => m.User?.Name ?? "")
      .filter(Boolean)
      .join(", "),
  }));

export const getDetailColumns = () => (
  <>
    <ExcelExportColumn field="ProjectId" title="Project ID" />
    <ExcelExportColumn field="ProjectName" title="Project" />
    <ExcelExportColumn field="LastWeekHours" title="Last Week Hours" />
    <ExcelExportColumn field="WeekendHours" title="Weekend Hours" />
    <ExcelExportColumn field="TotalHours" title="Total Hours" />
    <ExcelExportColumn field="CurrentWeekHours" title="Current Week Hours" />
    <ExcelExportColumn field="OwnersCSV" title="Owners" />
    <ExcelExportColumn field="DealTeamCSV" title="Deal Team" />
    <ExcelExportColumn
      field="LastUpdated"
      title="Last Updated"
      cellOptions={{ format: "mm/dd/yyyy" }}
    />
  </>
);

export const EditIconButton = ({ projectId, projectName }) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const history = useHistory();

  return (
    <div className={`edit-button-wrapper ${tooltipVisible ? "tooltip-open" : ""}`}>
      <Tooltip
        content={
          <span>
            Edit <span style={{ fontWeight: 500 }}>{projectName}</span> Deal
          </span>
        }
        relationship="label"
        positioning="above"
        onVisibleChange={(_, data) => setTooltipVisible(data.visible)}
      >
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ display: "inline-block" }}
        >
          <FluentButton
            icon={
              <FluentUiIcon
                name={hovered ? "EditFilled" : "EditRegular"}
                color="#0F6CBD"
                size={20}
              />
            }
            onClick={() =>
              history.push({
                pathname: `/projects/edit/${projectId}`,
                state: { fromDashboard: true },
              })
            }
            appearance="transparent"
            className="edit-hover-button"
          />
        </div>
      </Tooltip>
    </div>
  );
};

EditIconButton.propTypes = {
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
};

export const ProjectIdCell = ({ dataItem, canEdit }) => (
  <td className="badge-edit-cell background-white k-table-td">
    <div className="badge-wrapper">
      <TechBankerIdBadge text={dataItem.ProjectIdString} />
    </div>
    {canEdit && dataItem && (
      <EditIconButton
        projectId={dataItem.ProjectId}
        projectName={dataItem.ProjectName}
      />
    )}
  </td>
);

ProjectIdCell.propTypes = {
  dataItem: PropTypes.shape({
    ProjectId: PropTypes.number.isRequired,
    ProjectIdString: PropTypes.string.isRequired,
    ProjectName: PropTypes.string.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export const TechBankerEmptyState = ({canCreate}) => {
  const history = useHistory();

  return(
  <td colSpan={8} className="text-center k-table-td">
      <div className="empty-state-container">
        <img src={EmptyState} alt="No Data" />
        <p className="empty-state-header">
          Let’s get you on a Deal!{" "}
          <FluentUiIcon name="Rocket24Filled" color="#424242" size={20} />
        </p>
        <p className="empty-state-subtext">
          Any Deals you’re a Member of will show up here. Start a{" "}
          {canCreate ? (
            <button 
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
              className="create-deal-link"
              style={{backgroundColor: 'transparent', padding: 0}}
            >
              new one
            </button>
          ) : (
            <span className="create-deal-link disabled" aria-disabled="true">new one</span>
          )}{" "}
          or ping your Staffer to join an existing Deal.
        </p>
      </div>
    </td>
  )
}

TechBankerEmptyState.propTypes = {
  canCreate: PropTypes.bool.isRequired,
};

export const renderWhenData = (dataLength, renderFunction) => {
  return dataLength === 0 ? () => null : renderFunction;
};

export const renderCellContent = (dataLength, emptyStateContent, filledContent) => {
  return dataLength === 0 ? emptyStateContent : filledContent;
};

export const OWNER_TOOLTIP = "You are listed as a Project Owner on this Deal, therefore Banker hours cannot be entered here.";

export const isCurrentUserOwner = (members, userId) => {
  if (!userId || !Array.isArray(members)) return false;
  return members.some(
    (m) => (m.User?.UserId ?? m.User?.Id ?? m.UserId ?? m.Id) === userId && m.IsOwner
  );
};

export const OwnerDisabledCell = ({ value }) => (
  <td className="background-white k-table-td">
    <Tooltip content={OWNER_TOOLTIP} relationship="description" withArrow>
      <button
        tabIndex={0}
        style={{ display: "block", background: "none", border: "none", padding: 0, width: "100%" }}
        aria-label={OWNER_TOOLTIP}
      >
        <Input
          type="number"
          value={value === null || value === undefined ? "" : Number(value)}
          disabled
          appearance="outline"
          style={{ width: "100%", boxSizing: "border-box", pointerEvents: "none" }}
        />
      </button>
    </Tooltip>
  </td>
);

OwnerDisabledCell.propTypes = {
  value: PropTypes.number,
};